import { Fragment } from 'react';
import Link from 'next/link';
import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Button } from './Button';
import { Container } from './Container';
import { NavLink } from './NavLink';
import { getTranslation } from 'dictionary';
import { useUserContext } from 'context/UserContext';

function MobileNavLink({ href, children }) {
  return (
    <Popover.Button as={Link} href={href} className="block w-full p-2">
      {children}
    </Popover.Button>
  );
}

function MobileNavIcon({ open }) {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 overflow-visible stroke-slate-700"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <path
        d="M0 1H14M0 7H14M0 13H14"
        className={clsx(
          'origin-center transition',
          open && 'scale-90 opacity-0'
        )}
      />
      <path
        d="M2 2L12 12M12 2L2 12"
        className={clsx(
          'origin-center transition',
          !open && 'scale-90 opacity-0'
        )}
      />
    </svg>
  );
}

function MobileNavigation() {
  const { language } = useUserContext();
  return (
    <Popover>
      <Popover.Button
        className="relative z-10 flex h-8 w-8 items-center justify-center [&:not(:focus-visible)]:focus:outline-none"
        aria-label="Toggle Navigation"
      >
        {({ open }) => <MobileNavIcon open={open} />}
      </Popover.Button>
      <Transition.Root>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="duration-150 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Popover.Overlay className="fixed inset-0 bg-slate-300/50" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            as="div"
            className="absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-2xl bg-white p-4 text-lg tracking-tight text-slate-900 shadow-xl ring-1 ring-slate-900/5"
          >
            <MobileNavLink href="#primary-features">
              {getTranslation('landingpage', 'EXAMPLES', language)}
            </MobileNavLink>
            <MobileNavLink href="#secondary-features">
              {getTranslation('landingpage', 'HOW_IT_WORKS', language)}
            </MobileNavLink>
            <MobileNavLink href="#features">
              {getTranslation('landingpage', 'FEATURES', language)}
            </MobileNavLink>
            <MobileNavLink href="#faq">Preguntas frecuentes</MobileNavLink>
            <hr className="m-2 border-slate-300/40" />
            <MobileNavLink href="/login">
              {getTranslation('login', 'LOGIN', language)}
            </MobileNavLink>
          </Popover.Panel>
        </Transition.Child>
      </Transition.Root>
    </Popover>
  );
}

export function Header() {
  const { language } = useUserContext();
  return (
    <header className="py-10" id="header">
      <Container className="">
        <nav className="relative z-50 flex justify-between">
          <div className="flex items-center md:gap-4 lg:gap-12">
            <div className="mb-3">
              <span className="font-rochester text-logo px-1 logo font-bold w-auto">
                Simple
              </span>
            </div>
            <div className="hidden md:flex md:gap-4">
              <NavLink href="#primary-features">
                {getTranslation('landingpage', 'EXAMPLES', language)}
              </NavLink>
              <NavLink href="#secondary-features">
                {getTranslation('landingpage', 'HOW_IT_WORKS', language)}
              </NavLink>
              <NavLink href="#features">
                {getTranslation('landingpage', 'FEATURES', language)}
              </NavLink>
              <NavLink href="#faq">
                {getTranslation('landingpage', 'FAQ_HEADER', language)}
              </NavLink>
            </div>
          </div>
          <div className="flex items-center gap-x-5 md:gap-x-8">
            <div className="hidden md:block">
              <NavLink href="/login">
                {getTranslation('login', 'LOGIN', language)}
              </NavLink>
            </div>

            <Button href="/register">
              <span>{getTranslation('login', 'REGISTER', language)}</span>
            </Button>
            <div className="-mr-1 md:hidden">
              <MobileNavigation />
            </div>
          </div>
        </nav>
      </Container>
    </header>
  );
}
